import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { startOfMonth } from "date-fns";
import {
  DEPARTURE,
  clampViewStart,
  departureBlockReason,
  getMinimumStay,
  isBookedNight,
  isMonthVisible,
  isSelectableDay,
  nightsBetween,
} from "../../helper/stayRange";

const timeOf = (date) => (date ? date.getTime() : "");

// Calendar of a stay: picks the arrival and then the departure, explains days
// that cannot be picked, and shows the nights with a legend. Used in the date
// picker panel and inline on the page of an apartment.
const StayCalendar = ({
  activeField,
  arrival,
  departure,
  bookedDates = [],
  minimumStayMap = {},
  getDayClassName,
  renderDayContents,
  onDayClick,
  onClear,
  monthsShown = 1,
  className = "",
}) => {
  const { t } = useTranslation();
  const [hoveredDate, setHoveredDate] = useState(null);

  // The calendar keeps its own first month. react-datepicker otherwise
  // remembers which of two months was clicked and shifts the view when the
  // selected date changes, even to a past month. It is remounted after every
  // click and date change, so it always opens on this month.
  const activeDate = activeField === DEPARTURE ? departure || arrival : arrival || departure;
  const [viewStart, setViewStart] = useState(() => clampViewStart(activeDate));
  const [clicks, setClicks] = useState(0);
  const datesKey = `${timeOf(arrival)}-${timeOf(departure)}`;
  const [seenDatesKey, setSeenDatesKey] = useState(datesKey);
  if (seenDatesKey !== datesKey) {
    // Dates changed elsewhere stay in view; the calendar only moves to them
    // when neither date of the stay is shown.
    setSeenDatesKey(datesKey);
    const shown = [arrival, departure].some((date) =>
      isMonthVisible(date, viewStart, monthsShown)
    );
    if (!shown && activeDate) setViewStart(clampViewStart(activeDate));
  }
  const handleDayClick = (date) => {
    setClicks((count) => count + 1);
    onDayClick(date);
  };

  useEffect(() => {
    setHoveredDate(null);
  }, [activeField]);

  const isSelectable = (date) =>
    isSelectableDay({
      activeField,
      date,
      arrivalDate: arrival,
      bookedDates,
      minimumStayMap,
    });

  const dayClassName = (date) => {
    const classes = getDayClassName ? getDayClassName(date) : "";
    if (activeField !== DEPARTURE || !isBookedNight(date, bookedDates)) {
      return classes;
    }

    // The first booked day can still be the departure day.
    return isSelectable(date)
      ? classes.replace("react-datepicker__day--booked", "").trim()
      : classes;
  };

  // Unavailable departure days explain themselves on hover. While picking the
  // departure, the minimum stay of later days as arrivals does not apply.
  const dayContents = (day, date) => {
    const content = renderDayContents ? renderDayContents(day, date) : day;
    if (activeField !== DEPARTURE || !arrival || nightsBetween(arrival, date) === 0) {
      return content;
    }

    const reason = departureBlockReason({
      arrivalDate: arrival,
      date,
      bookedDates,
      minimumStayMap,
    });
    if (!reason) return <span>{day}</span>;

    const label =
      reason === "minimum_stay"
        ? t("minimum_stay_blocked", {
            count: getMinimumStay(arrival, minimumStayMap),
            defaultValue: "Too short, minimum nights: {{count}}",
          })
        : t("booked_night_blocked", "The stay would include a booked night");

    return (
      <span
        className='date-tooltip stay-date-day--blocked'
        data-tooltip={label}
        aria-label={`${day}. ${label}`}
      >
        {day}
      </span>
    );
  };

  const minimumStay = arrival ? getMinimumStay(arrival, minimumStayMap) : 1;
  const previewDeparture =
    activeField === DEPARTURE && hoveredDate && isSelectable(hoveredDate)
      ? hoveredDate
      : departure;
  const nights = nightsBetween(arrival, previewDeparture);

  return (
    <div
      className={`stay-calendar rental-dp-calender-main ${
        monthsShown > 1 ? "stay-calendar--months" : ""
      } ${className}`}
    >
      <DatePicker
        key={`${clicks}-${datesKey}-${monthsShown}`}
        inline
        selected={activeField === DEPARTURE ? departure : arrival}
        openToDate={clampViewStart(viewStart)}
        onMonthChange={(date) => setViewStart(clampViewStart(startOfMonth(date)))}
        onChange={handleDayClick}
        selectsStart={activeField !== DEPARTURE}
        selectsEnd={activeField === DEPARTURE}
        startDate={arrival}
        endDate={departure}
        minDate={new Date()}
        filterDate={isSelectable}
        dayClassName={dayClassName}
        renderDayContents={dayContents}
        selectsDisabledDaysInRange
        onDayMouseEnter={(date) => setHoveredDate(date)}
        onMonthMouseLeave={() => setHoveredDate(null)}
        monthsShown={monthsShown}
      />
      <div className='stay-date-panel__footer'>
        <div className='stay-date-panel__stay'>
          {nights > 0 && (
            <strong>
              {t("stay_nights", {
                count: nights,
                defaultValue: "Nights: {{count}}",
              })}
            </strong>
          )}
          {activeField === DEPARTURE && minimumStay > 1 && (
            <span>
              {t("minimum_stay_hint", {
                count: minimumStay,
                defaultValue: "Minimum nights: {{count}}",
              })}
            </span>
          )}
        </div>
        {onClear && (arrival || departure) && (
          <button type='button' className='stay-date-panel__clear' onClick={onClear}>
            {t("clear_dates", "Clear dates")}
          </button>
        )}
      </div>
      <ul className='stay-date-legend listUnstyled'>
        <li>
          <span className='stay-date-legend__mark stay-date-legend__mark--today' />
          {t("legend_today", "Today")}
        </li>
        <li>
          <span className='stay-date-legend__mark stay-date-legend__mark--booked' />
          {t("legend_booked", "Booked")}
        </li>
        <li>
          <span className='stay-date-legend__mark stay-date-legend__mark--stay' />
          {t("legend_your_stay", "Your stay")}
        </li>
      </ul>
    </div>
  );
};

export default StayCalendar;
